import { Link } from "@remix-run/react";
import { Phone, ChevronDown } from "lucide-react";
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuList,
    NavigationMenuTrigger,
} from "~/components/ui/navigation-menu";
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from "~/components/ui/drawer";
import { getFlagUrl, regionConfigs } from "custom-config/region.configs";
import { type RegionConfig } from "~/store/regionConfigStore";

interface GlobalPhoneProps {
    isMobile: boolean;
    regionConfig: RegionConfig;
}

export default function GlobalPhone({ isMobile, regionConfig }: GlobalPhoneProps) {

    if (!regionConfig?.phoneNumber) return null;

    if (isMobile) {
        return (
            <Drawer>
                <DrawerTrigger asChild>
                    <button className="flex items-center gap-1.5 pl-2.5 pr-1.5 rounded-full bg-gray-100/80 h-[32px] select-none">

                        <Phone size={14} className="text-oh-black" strokeWidth={1.5} />
                        <span className="text-[13px] font-medium text-oh-black">
                            {regionConfig?.phoneNumber}
                        </span>
                        <ChevronDown size={12} className="text-zinc-400" strokeWidth={2} />
                    </button>
                </DrawerTrigger>
                <DrawerContent className="max-w-xl w-full max-h-[85dvh] flex flex-col mx-auto overflow-y-auto">
                    <DrawerHeader className="px-4">
                        <DrawerTitle className="text-base font-bold text-zinc-900 ">Global Contact Numbers</DrawerTitle>
                    </DrawerHeader>
                    <div className="flex flex-col gap-3 px-4 pb-8">
                        <Link
                            to={`tel:${regionConfig.phoneNumberWithCode || regionConfig.phoneNumber}`}
                            className="flex items-center gap-4 py-4 px-4 rounded-xl bg-zinc-50 border border-zinc-100 active:scale-[0.98] transition-all"
                        >
                            <img
                                src={getFlagUrl(regionConfig.region)}
                                alt={regionConfig.country}
                                className="w-10 h-10 object-cover rounded-md shadow-sm"
                            />
                            <div className="flex flex-col">
                                <span className="text-base font-bold text-zinc-900">
                                    {regionConfig.phoneNumberWithCode || regionConfig.phoneNumber}
                                </span>
                                <span className="text-[12px] text-zinc-500">{regionConfig.country} (Current)</span>
                            </div>
                            <div className="ml-auto">
                                <Phone size={20} className="text-zinc-900" />
                            </div>
                        </Link>
                        <div className="h-px bg-zinc-100 my-1" />
                        {Object.values(regionConfigs)
                            .filter((r: any) => r.phoneNumber && r.phoneNumberWithCode && r.region !== regionConfig?.region)
                            .map((r: any) => (
                                <Link
                                    key={r.region}
                                    to={`tel:${r.phoneNumberWithCode}`}
                                    className="flex items-center gap-4 py-4 rounded-xl hover:bg-zinc-50 border border-transparent active:border-zinc-100 active:scale-[0.98] transition-all"
                                >
                                    <img
                                        src={getFlagUrl(r.region)}
                                        alt={r.country}
                                        className="w-10 h-10 object-cover rounded-md shadow-sm"
                                    />
                                    <div className="flex flex-col">
                                        <span className="text-base font-semibold text-zinc-800">{r.phoneNumberWithCode}</span>
                                        <span className="text-[12px] text-zinc-400">{r.country}</span>
                                    </div>
                                    <div className="ml-auto opacity-40">
                                        <Phone size={18} />
                                    </div>
                                </Link>
                            ))}
                    </div>
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <NavigationMenu>
            <NavigationMenuList>
                <NavigationMenuItem value="phone">
                    <NavigationMenuTrigger
                        className="bg-transparent hover:bg-transparent p-0 data-[state=open]:bg-transparent [&>svg]:hidden"
                    >
                        <div className="group flex items-center gap-1.5 pl-2.5 pr-1.5 rounded-full bg-gray-100/80 hover:bg-gray-200/80 transition-all duration-200 cursor-pointer h-[32px] select-none">
                            <Link
                                to={`tel:${regionConfig?.phoneNumberWithCode || regionConfig?.phoneNumber}`}
                                prefetch="intent"
                                className="flex items-center gap-1.5"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Phone size={14} className="text-oh-black" strokeWidth={1.5} />
                                <span className="text-[13px] font-medium text-oh-black transition-colors">
                                    {regionConfig?.phoneNumber}
                                </span>
                            </Link>
                            <ChevronDown
                                size={12}
                                className="text-zinc-400 group-hover:text-zinc-600 transition-all duration-200 group-data-[state=open]:rotate-180"
                                strokeWidth={2}
                            />
                        </div>
                    </NavigationMenuTrigger>
                    <NavigationMenuContent className="md:left-auto md:right-0">
                        <div className="w-[260px] max-h-[550px] overflow-y-auto p-2">
                            <div className="px-3 py-2 border-b border-zinc-100 mb-1">
                                <p className="text-lg font-semibold tracking-tight">Global Contact Numbers</p>
                            </div>
                            <Link
                                to={`tel:${regionConfig?.phoneNumberWithCode || regionConfig?.phoneNumber}`}
                                className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-zinc-50 transition-all duration-200 group"
                            >
                                <img
                                    src={getFlagUrl(regionConfig?.region)}
                                    alt={regionConfig?.country}
                                    className="w-8 h-8 object-cover rounded-sm"
                                />
                                <div className="flex flex-col">
                                    <span className="text-sm font-semibold text-zinc-900">{regionConfig?.phoneNumberWithCode || regionConfig?.phoneNumber}</span>
                                    <span className="text-[12px] text-zinc-500">{regionConfig?.country} (Current)</span>
                                </div>
                                <div className="ml-auto">
                                    <Phone size={14} className="text-zinc-900" />
                                </div>
                            </Link>
                            <div className="h-px bg-zinc-100 my-1" />
                            {Object.values(regionConfigs)
                                .filter((r: any) => r.phoneNumber && r.phoneNumberWithCode && r.region !== regionConfig?.region)
                                .map((r: any) => (
                                    <Link
                                        key={r.region}
                                        to={`tel:${r.phoneNumberWithCode}`}
                                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-zinc-50 transition-all duration-200 group"
                                    >
                                        <img
                                            src={getFlagUrl(r.region)}
                                            alt={r.country}
                                            className="w-8 h-8 object-cover rounded-sm group-hover:grayscale-0 transition-all"
                                        />
                                        <div className="flex flex-col">
                                            <span className="text-sm font-semibold text-zinc-800">{r.phoneNumberWithCode}</span>
                                            <span className="text-[12px] text-zinc-400">{r.country}</span>
                                        </div>
                                        <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Phone size={14} className="text-oh-black opacity-40" />
                                        </div>
                                    </Link>
                                ))}
                        </div>
                    </NavigationMenuContent>
                </NavigationMenuItem>
            </NavigationMenuList>
        </NavigationMenu>
    );
}
